import { Badge } from '@ttab/elephant-ui'
import { CheckIcon, EyeIcon, FolderIcon } from '@ttab/elephant-ui/icons'
import { decodeString } from '@/lib/decodeString'
import type { Wire } from '@/shared/schemas/wire'
import { Editor } from '@/components/PlainEditor'
import { getWireStatus } from '@/lib/getWireStatus'
import { DocumentHistory } from './DocumentHistory/DocumentHistory'
import { useState, useEffect } from 'react'
import { getDocumentState } from '@/lib/getDocumentState'
import { useTranslation } from 'react-i18next'

export const WirePreview = ({ wire }: {
  wire: Wire
}) => {
  const { t } = useTranslation('wires')
  const sourceTitle = wire?.fields['document.rel.source.title']?.values[0]
  const sourceUri = wire?.fields['document.rel.source.uri']?.values[0]
  const providerTitle = wire?.fields['document.rel.provider.title']?.values[0]
  const providerUri = wire?.fields['document.rel.provider.uri']?.values[0]
  const data = {
    source: sourceTitle || sourceUri?.replace('wires://source/', ''),
    provider: providerTitle || providerUri?.replace('wires://provider/', ''),
    role: wire?.fields['document.meta.tt_wire.role'].values[0],
    newsvalue: wire?.fields['document.meta.core_newsvalue.value']?.values[0],
    status: getWireStatus(wire),
    version: wire?.fields['current_version']?.values[0] ? BigInt(wire.fields['current_version'].values[0]) : 1n,
    wireState: getDocumentState(wire)
  }

  const [wireVersion, setWireVersion] = useState<bigint | undefined>(undefined)

  useEffect(() => {
    setWireVersion(undefined)
  }, [wire.id])

  return (
    <div className='grid grid-rows-[auto_auto_1fr] max-w-full w-full h-full overflow-y-auto mx-auto'>
      <div className='flex items-center h-10 px-12'>
        <div className='text-sm flex flex-row gap-2'>
          {data?.source && (
            <Badge className='h-6 pointer-events-none'>
              {decodeString(data.source)}
            </Badge>
          )}

          {data?.provider && data.provider.toLocaleLowerCase() !== data.source?.toLocaleLowerCase() && (
            <Badge className='h-6'>
              {decodeString(data.provider)}
            </Badge>
          )}

          {data?.role === 'pressrelease' && (
            <Badge className='h-6 bg-gray-400 pointer-events-none'>
              {t('preview.pressRelease')}
            </Badge>
          )}

          {data?.newsvalue === '6' && (
            <Badge variant='destructive' className='h-6 pointer-events-none'>
              {t('history.status.flash')}
            </Badge>
          )}

          {data?.status && data.status === 'read' && (
            <Badge className='h-6 pe-2 bg-approved-background text-foreground border border-approved pointer-events-none'>
              <span className='me-2'>{t('preview.statusRead')}</span>
              <EyeIcon size={12} fill='oklch(96.62% 0.0108 149.86)' />
            </Badge>
          )}

          {data?.status && data.status === 'saved' && (
            <Badge className='h-6 pe-2 bg-done-background text-foreground border border-done pointer-events-none'>
              <span className='me-2'>{t('preview.statusSaved')}</span>
              <FolderIcon size={12} fill='oklch(98.51% 0.0264 99.9)' />
            </Badge>
          )}

          {data?.status && data.status === 'used' && (
            <Badge className='h-6 pe-2 bg-usable-background text-foreground border border-usable pointer-events-none'>
              <span className='me-2'>{t('preview.statusUsed')}</span>
              <CheckIcon size={12} fill='oklch(95.05% 0.022 263.19)' />
            </Badge>
          )}
        </div>
      </div>

      <div className='mx-12 border mt-2 py-1.5 px-3 rounded'>
        <DocumentHistory
          uuid={wire.id}
          currentVersion={data?.version}
          documentState={data.wireState}
          onSelectVersion={setWireVersion}
          selectedVersion={wireVersion}
        />
      </div>

      <Editor
        id={wire.id}
        version={wireVersion}
        textOnly={false}
        direct
        disableScroll={true}
        showNotes={true}
      />
    </div>
  )
}
